// @react
import React, { ReactElement } from 'react'
// @types
// @libraries
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
// @components
import colors from '@bit/atd.web.colors'
import Grid from '@bit/atd.web.grid'
import Button from '@bit/atd.web.button'
import Spacer from '@bit/atd.web.spacer'
import Icon from '@bit/atd.web.icons'
// @card parts
import Wrap from '../../Parts/Wrap'
import Title from '../../Parts/Title'
import Avatar from '../../Parts/Avatar'
import { PropsType as CardProps } from '../../index'

const useStyles = makeStyles({
  wrap: {
    border: 'none',
    borderBottom: '1px solid #DBD9D6',
    borderRadius: 0,
    boxShadow: 'none',
    padding: '16px 0px',
    alignItems: 'flex-start',
  },
  header: {
    display: 'flex',
    width: '100%',
    height: '100%',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  avatarWrapper: {
    '& + $staffWrapper + $textWrapper': {
      marginTop: '12px',
    },
  },
  textWrapper: {
    height: '100%',
    minWidth: '155px',
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  title: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    color: '#E13000',
    '& + $subtitle': {
      marginTop: '2px',
    },
  },
  subtitle: {
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    color: '#7c7c7c',
  },
  button: {
    marginTop: '16px',
    maxWidth: '104px',
    '& > span': {
      width: 'auto',
    },
  },
  isNoAvatar: {
    '& $title': {
      fontSize: '18px',
      lineHeight: '24px',
      '& + $subtitle': {
        marginTop: '4px',
      },
    },
    '& $subtitle': {
      fontSize: '14px',
      lineHeight: '16px',
    },
  },
  horizontalLayout: {
    flexWrap: 'nowrap',
    '& $textWrapper': {
      padding: '0 0 0 12px',
      marginBottom: '0',
    },
  },
})

type PropsType = {
  following?: boolean
  /** Auto layout width - number */
  avatar?: CardProps['avatar']
  title: string | ReactElement
  titleURL?: string
  subtitle?: string | ReactElement
  btnLabel: string
  onButtonClick?: () => void
  titleTargetUrl?: string
}

const CardWidgetFollow = ({
  following,
  avatar,
  title,
  titleURL,
  subtitle,
  btnLabel,
  onButtonClick,
  titleTargetUrl,
}: PropsType): ReactElement => {
  const classes = useStyles()

  return (
    <Wrap
      className={clsx(classes.wrap, {
        [classes.isNoAvatar]: !avatar,
      })}
      tabIndex={0}
      role="button"
    >
      <Grid
        container
        className={clsx(classes.header, classes.horizontalLayout)}
      >
        {avatar && (
          <Grid item className={classes.avatarWrapper}>
            <div>
              <Avatar
                size={avatar.size}
                imgSrc={avatar.src}
                imgAlt={avatar.alt}
                bgColor={avatar.bgColor}
              >
                {avatar.textAs && avatar.textAs}
              </Avatar>
            </div>
          </Grid>
        )}

        <Grid item xs className={classes.textWrapper}>
          {/* Title */}
          <Typography className={classes.title}>
            <Title title={title} titleURL={titleURL} target={titleTargetUrl} />
          </Typography>

          {/* SubTitle */}
          {subtitle && (
            <Typography className={classes.subtitle}>{subtitle}</Typography>
          )}
          <Button
            variant={following ? 'secondary' : 'primary'}
            size="small"
            onClick={onButtonClick}
            className={classes.button}
          >
            <>
              {following ? (
                <Icon color={colors.ctaPrimary} glyph="subtract" size={16} />
              ) : (
                <Icon glyph="add" size={16} />
              )}
              <Spacer size={4} />
              {btnLabel}
            </>
          </Button>
        </Grid>
      </Grid>
    </Wrap>
  )
}

export default CardWidgetFollow
